import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toExportCanvas } from '../../components/exportCanvas';
import { tooltipFlipTransform } from '../../components/tooltipPlacement';
import { useOrbitInteraction } from '../../interaction/useOrbitInteraction';
import { useCameraFlight } from '../../interaction/useCameraFlight';
import { frameNetworkSphere } from '../render/frameNetworkSphere';
import { srgbToLinear, toLinearLighting } from '../../render/lighting';
import { requestGpuDevice } from '../../render/webgpu/surfaceRenderer';
import type { Size, ViewCamera } from '../../types';
import { toRelativeNodeSizes } from '../graph/nodeSizes';
import { useSettlingLayout } from '../layout/useSettlingLayout';
import { toCentroid } from '../layout/groupVolumes';
import type { Network3DViewSettings } from '../networkViewSettings';
import { findNetworkNodeAt } from '../render/findNetworkNodeAt';
import { locateNetworkMark } from '../render/locateNetworkMark';
import { fitNetworkZoom, NETWORK_FIT_INSET } from '../render/fitNetworkZoom';
import type { NetworkTheme } from '../render/networkTheme';
import { placeNetworkLabels } from '../render/placeNetworkLabels';
import {
  isSameFocus,
  packNetworkInstances,
  type NetworkFocus,
} from '../render/packNetwork';
import { useGpuCanvas } from '../render/useGpuCanvas';
import {
  EDGE_LEAD_STRENGTH,
  FLOW_PIXELS_PER_SECOND,
} from '../render/webgpu/networkUniforms';
import { prefersReducedMotion } from '../../reducedMotion';
import { useLayoutTransition } from './useLayoutTransition';
import {
  createNetworkGpuRenderer,
  type NetworkGpuRenderer,
} from '../render/webgpu/networkRenderer';
import type {
  NetworkGraph,
  NetworkLayout,
  NetworkNode,
  NetworkNodeId,
  NetworkVolume,
} from '../types';
import { findNetworkVolumeAt } from '../render/findNetworkVolumeAt';
import { findNetworkRouteAt } from '../render/findNetworkRouteAt';
import {
  toAccountedMarkCount,
  type NetworkFolded,
} from '../render/foldedMarks';
import { NetworkMiniature } from './NetworkMiniature';
import { MarkList, type MarkListColors } from '../../interaction/MarkList';
import { useMarkFocus } from '../../interaction/useMarkFocus';
import { HOVER_FADE, useSettledHover } from '../../interaction/useSettledHover';
import {
  toggleId,
  toNetworkMarkIndices,
  toNetworkTreeFocus,
  toNetworkTreeItems,
  toTreeToggle,
} from './networkMarkTree';
import { toMarkFilter } from '../../interaction/markFilter';

/**
 * A 3D network view over a graph, drawn through WebGPU.
 *
 * The shape follows `Surface3DChart`: a canvas, the shared orbit hook, and a memoised pipeline from
 * data to instance buffers. What differs is that the whole frame is on the GPU - there is no
 * software path here, unlike the surface, and an adapterless browser gets a message rather than a
 * slower picture. That is a deliberate limit of the experiment and not a decision about the
 * product: a rasteriser for a few hundred shaded impostors and a few thousand ribbons is a second
 * renderer's worth of work, and the point of this was to find out what the hardware path buys.
 */

/** Hit target padding, in CSS pixels. Larger than the mark, per the interaction spec. */
const HIT_TOLERANCE = 5;

/**
 * How far from a reference still counts as on it, in pixels.
 *
 * Wider than `HIT_TOLERANCE`, which is a mark's. A reference is a little over a pixel wide with no
 * interior to aim at, and overshooting costs nothing - it has no neighbours crowding it the way
 * marks in a cluster do, and a mark under the pointer is resolved first either way.
 */
const ROUTE_HIT_TOLERANCE = 7;

/**
 * The most labels drawn at once.
 *
 * A ceiling on the clutter first and on the DOM second. Past roughly this many the text competes
 * with the marks for the reader's attention rather than explaining them, whatever the declutter
 * does about overlap - and the cap is what makes the picture legible at a few thousand marks
 * rather than merely unoverlapped.
 */
const MAXIMUM_LABELS = 28;

/** Small, because a label is an annotation on a mark rather than content in its own right. */
const LABEL_FONT_SIZE = 11;

/**
 * Where a label is truncated, in pixels.
 *
 * Here rather than in `placeNetworkLabels`, for the reason the font size is: the caller's CSS is
 * what actually truncates, and the declutter only needs to be told so its estimated boxes stop
 * growing at the same point. Held in two places they would diverge silently - the algorithm
 * would reject labels it could have kept, or place ones that overlap on screen.
 */
const LABEL_MAX_WIDTH = 168;

/**
 * The thumbnail's box inside a tooltip, in CSS pixels.
 *
 * Wider than tall because a force solve settles into something wider than it is tall once
 * projected, and a square box would leave a band of surface above and below it. Small enough that
 * the tooltip stays a tooltip: past about twice this it is a panel that happens to follow the
 * cursor, and it starts covering the group it is describing.
 */
const MINIATURE_SIZE = { width: 200, height: 140 } as const;

/** Shared, so a search does not rebuild the tree on every render by handing it a fresh set. */
const NO_COLLAPSED_IDS: ReadonlySet<NetworkNodeId> = new Set();

/**
 * Every word this view puts on screen, supplied by the host.
 *
 * Same rule as `ariaLabel` and `exportCaptions`, for the same reason: this package has no locale,
 * so a string written here is English for everybody. The tooltip is where that mattered most - it
 * is the most-read text the view has, once per hover - but the device messages are the same defect
 * at lower traffic.
 *
 * Required rather than defaulted. A default is English, and an English default is exactly the
 * thing that goes unnoticed until a German reader hovers a node.
 */
export type Network3DViewLabels = {
  /** The reference count beside a hovered node's type, already pluralised. */
  degree: (count: number) => string;
  /** The component count beside a hovered group's name, already pluralised. */
  members: (count: number) => string;
  /** What a double-click over an open group does, shown at the foot of its tooltip. */
  collapseHint: string;
  /** What a double-click over a closed group does. */
  expandHint: string;
  /** Shown when the browser offers no WebGPU adapter at all. */
  noAdapter: string;
  /** Shown when a working device was reclaimed, where the answer is to reload. */
  deviceLost: string;
  /** The accessible name and heading of the groups-and-components tree after the canvas. */
  markTree: string;
};

/**
 * What a host may do to this view's camera.
 *
 * Commands rather than state: a host that could set the camera would have to know the viewport and
 * the layout to compute one, and both are this side's.
 */
export type NetworkCameraCommands = {
  /** Frames the whole layout, as the view opened. */
  fit: VoidFunction;
  /** Frames the region drawn for one group, easing rather than cutting. */
  frameGroup: (nodeId: NetworkNodeId) => void;
  /**
   * Whether `frameGroup` would do anything for this node.
   *
   * Asked so a host can offer framing only where there is something to frame. Not derivable
   * upstream: which groups get a region is `toGroupVolumes`' decision and it leaves out a group of
   * one and a group with no marks beneath it, neither of which a host can see from the graph.
   */
  canFrameGroup: (nodeId: NetworkNodeId) => boolean;
};

export type Network3DViewProps = {
  graph: NetworkGraph;
  settings: Network3DViewSettings;
  theme: NetworkTheme;
  camera: ViewCamera;
  labels: Network3DViewLabels;
  /**
   * Required, not optional.
   *
   * `useOrbitInteraction` puts `role="img"` on the canvas and deliberately supplies no name, being
   * unable to know what is on screen. A focusable graphic that announces nothing is worse than an
   * unlabeled one, so the caller has to say.
   */
  ariaLabel: string;
  /** Seed for the force layouts, so a caller can ask for a different settling of the same graph. */
  seed: number;
  /**
   * Publishes the view's export hook once there is a drawn canvas, and null when it goes.
   *
   * A callback rather than a ref the host fills, so the host cannot hold a hook that outlives the
   * canvas it reads. Optional: the stories have nothing to export to.
   */
  onExportCanvasAvailable?: (
    createExportCanvas: (() => OffscreenCanvas | null) | null
  ) => void;
  /**
   * Publishes how to find a mark on screen, for anything anchored to one from outside the canvas.
   *
   * A DOM view can be measured; this one has to be asked. Published the same way as the export
   * hook and for the same reason - the host cannot be left holding an answer that outlives the
   * canvas it came from.
   */
  onMarkLocatorAvailable?: (locator: NetworkMarkLocator | null) => void;
  /**
   * Publishes a way to put the camera back where the view would have put it.
   *
   * Published rather than taken as a prop, because the camera is this component's: a host passing
   * one in would be supplying the answer rather than asking the question, and the fit is computed
   * from a viewport only this side has measured. Same shape as the two hooks above, so a host
   * cannot hold an action that outlives the canvas it moves.
   */
  onCameraCommandsAvailable?: (commands: NetworkCameraCommands | null) => void;
  /**
   * The header lines the export has to carry, since the exported file is the canvas alone.
   *
   * Passed in rather than built here: they are the host's own DOM header, translated, and this
   * package has no locale.
   */
  exportCaptions?: readonly string[];
  /**
   * Whether there is anything to export, following `Surface3DChart`'s prop of the same name.
   *
   * False for the whole session on a browser with no WebGPU adapter, where this draws a message
   * rather than a canvas. The host cannot work that out for itself - it sees a viewpoint that
   * loaded components, so its own emptiness check says drawable - and without it the Export to
   * PNG entry stayed enabled and threw `export canvas not found` from a menu that looked fine.
   *
   * Not the transient window before the first frame, which closes on its own. An absent adapter
   * does not.
   */
  onDrawableChange?: (isDrawable: boolean) => void;
  /**
   * A double-click resolved to the node it landed on, by id.
   *
   * One callback for both kinds of target, because both resolve to a node: a mark is one, and a
   * region carries the node it stands for. What the host does with it is the host's - this view
   * has no notion of a group beyond the geometry, and inventing one here would be a second
   * opinion about a structure the host declared.
   */
  onNodeActivate?: (nodeId: NetworkNodeId) => void;
  /**
   * What a mark is standing in for, or `null` where it stands only for itself.
   *
   * How a host says "this mark is a closed group" without this package growing a notion of one.
   * A closed group is drawn as a mark like any other - it has a weight and a position and no
   * region, because its contents are not on screen - so nothing in the graph distinguishes it,
   * and the only thing that can is the host that closed it.
   *
   * Absent for a host with no collapsing, where every tooltip is about something already visible.
   */
  foldedBehind?: (nodeId: NetworkNodeId) => NetworkFolded | null;
  /**
   * What the reader is looking for, matched against every mark and group label.
   *
   * Blank is not a search: the tree keeps every row and nothing is lit, which is a different
   * picture from a search that found nothing. The host owns the field and the text - the view
   * owns what the text means, because it owns the tree and the marks it narrows.
   */
  query?: string;
  /** How many rows the query matched, so the host's field can say. Called on every change. */
  onMatchCountChange?: (count: number) => void;
  /**
   * A secondary click resolved to what it landed on, for the host to open a menu over.
   *
   * In client coordinates rather than canvas-local, because a menu is positioned against the
   * viewport rather than against this element - and the host is the only thing that knows where
   * its menu goes.
   *
   * The host decides whether there is anything to offer; this only says what was hit. Returning
   * a target the host has no menu for is normal, and the default browser menu is suppressed
   * either way, since a canvas has nothing useful behind it.
   */
  onContextMenu?: (
    target: NetworkContextTarget,
    at: { clientX: number; clientY: number }
  ) => void;
};

/**
 * What a secondary click landed on.
 *
 * A node id for a mark and for a region - a region reports the group it stands for, and the host
 * resolves both the same way, since whether that node is a component is a question only the host
 * can answer. An *index into `graph.edges`* for a reference, because the package holds no identity
 * for one: `NetworkEdge` carries how a reference is drawn and nothing about what it is.
 */
export type NetworkContextTarget =
  | { kind: 'node'; nodeId: NetworkNodeId }
  | { kind: 'edge'; edgeIndex: number };

/**
 * The region drawn for a group, by the id of the node it stands for.
 *
 * By id rather than by index, because an index belongs to one render and a host asking is
 * answering a menu the reader opened some renders ago. `undefined` for a group whose region is no
 * longer drawn - collapsed, or the grouping changed under the open menu - and for every node that
 * never had one, which is the same answer to both: there is nothing there to frame.
 */
const toGroupVolume = (
  layout: NetworkLayout,
  graph: NetworkGraph,
  nodeId: NetworkNodeId
): NetworkVolume | undefined =>
  layout.volumes.find(({ nodeIndex }) => graph.nodes[nodeIndex]?.id === nodeId);

/**
 * The camera that frames one group's region, from whichever camera is in effect.
 *
 * One function because two callers have to agree: the flight lands on this, and the next resize
 * re-frames to it. Computed twice, a changed inset would make the first resize after a flight
 * jump to a different framing than the one it had just flown to.
 */
const toFramedGroupCamera = (
  camera: ViewCamera,
  viewport: Size,
  volume: NetworkVolume
): ViewCamera =>
  frameNetworkSphere({
    camera,
    viewport,
    center: volume.center,
    radius: volume.radius,
    inset: NETWORK_FIT_INSET,
  });

/**
 * The host's camera, turned the way the reader has turned it.
 *
 * Anything this view recomputes - a fit, a re-framing after a resize - starts from the camera the
 * host supplied, which carries the angle the host opened at and knows nothing of the reader's.
 * That was harmless while the only thing that recomputed was a fit the reader had not touched:
 * `hasReaderMovedCamera` kept the held camera instead. A flight breaks that, because it sets the
 * camera and so clears the latch while leaving the reader's angle in effect - so without this, a
 * reader who turned the estate, framed a group and then dragged the pane divider would find it
 * turned back to the angle it opened at.
 *
 * Only the angle. The target and the zoom are what a framing is *for*, and the reader's zoom is
 * the thing being replaced.
 */
const toReaderOriented = (
  camera: ViewCamera,
  live: ViewCamera | null
): ViewCamera => {
  if (live === null) return camera;
  if ('orientation' in camera && 'orientation' in live) {
    return { ...camera, orientation: live.orientation };
  }
  if (!('orientation' in camera) && !('orientation' in live)) {
    return { ...camera, yaw: live.yaw, pitch: live.pitch };
  }
  /* Two different kinds of camera, which only a host swapping one for the other produces. The
     host's is the answer: the reader's angle does not survive a change of what an angle is. */
  return camera;
};

/**
 * The node a target stands for. A reference does not have one.
 *
 * Exhaustive over `NetworkFocus`, so a kind added to it is a compile error here rather than an
 * index read against the wrong array.
 */
const toTargetNodeIndex = (
  target: NetworkFocus,
  volumes: readonly NetworkVolume[]
): number | undefined => {
  switch (target.kind) {
    case 'node':
      return target.index;
    case 'region':
      return volumes[target.index]?.nodeIndex;
    case 'edge':
    /* A set of search results is not a thing to activate: a double-click lands on whatever is
       under the pointer, and that is a mark or a region, never the answer to a query. */
    case 'marks':
      return undefined;
    default: {
      /* `noImplicitReturns` is off, so a missing case would otherwise fall out of the switch as
         `undefined` and read the wrong array somewhere else. This makes a new kind a compile
         error here, which is what the switch is for. */
      const unreachable: never = target;
      return unreachable;
    }
  }
};

export type NetworkMarkLocator = {
  /** Which mark the view draws at a point, in client coordinates. Regions are not marks. */
  at: (point: { clientX: number; clientY: number }) => NetworkNodeId | null;
  /** Where the view draws a mark now, in client coordinates, or `null` if it cannot say. */
  locate: (nodeId: NetworkNodeId) => { x: number; y: number } | null;
};

type Hover = {
  /**
   * What the pointer is over: a mark, or the space a group occupies.
   *
   * The packer's own `NetworkFocus` rather than a union of the same shape, because that is where
   * this goes - `focus: hover?.target`. Two structurally identical declarations line up only by
   * coincidence, and a third kind added to one of them leaves the other silently behind.
   *
   * Resolved marks-first and never merged. A region is almost always also *around* something, so
   * a single search would put the two on one depth tie-break and a cloud drawn in front of a mark
   * would take the pointer aimed at the mark.
   */
  target: NetworkFocus;
  /** Anchor in CSS pixels within the canvas. */
  at: { x: number; y: number };
};

/**
 * Acquires the device, and draws nothing itself.
 *
 * Split from the scene so that a browser without an adapter does not run the pipeline it cannot
 * present. The early return for those states used to sit below every memo, which is to say below
 * `buildNetworkLayout` and `packNetworkInstances` - so a view showing one sentence still paid a
 * full force solve and allocated tens of megabytes of instance buffers, and paid it again on every
 * later rebuild, because `absent` and `lost` are terminal and the rebuild triggers are ordinary
 * ones: a formatting rule from another pane, a graph re-emission, the layout dropdown behind the
 * message. `useLayoutTransition` made it worse by animating that nothing over 450ms.
 *
 * A guard here cannot fix it, because hooks cannot be skipped. Moving the work into a child that
 * only exists when there is something to draw into can, and does.
 */
/**
 * The camera that frames the whole layout, which is the view's opening shot and what its fit
 * control puts back.
 *
 * One function because two callers need the same answer: the memo that supplies a camera while the
 * reader has not taken it, and the control that commands one after they have. Computed in two
 * places they could drift, and the drift would read as the button framing it slightly differently
 * from the way the view opened.
 */
const toFittedCamera = (
  camera: ViewCamera,
  viewport: Size,
  layout: NetworkLayout
): ViewCamera => ({
  ...camera,
  zoom:
    camera.zoom *
    fitNetworkZoom({ camera, viewport, layout, inset: NETWORK_FIT_INSET }),
});

export const Network3DView = ({
  labels,
  theme,
  onDrawableChange,
  ...scene
}: Network3DViewProps) => {
  const [device, setDevice] = useState<GPUDevice | null>(null);
  /**
   * `lost` is its own state rather than folded into `absent`.
   *
   * They need different sentences. "This browser has no adapter" is false of a device that was
   * working a moment ago and was reclaimed, and telling a reader to try another browser when the
   * answer is to reload is worse than saying nothing.
   */
  const [deviceState, setDeviceState] = useState<
    'pending' | 'ready' | 'absent' | 'lost'
  >('pending');

  /* Through a ref for the reason `Surface3DChart` gives about its own: this is a notification
     sink, and an inline callback in a dependency list re-sends on every render of the host. */
  const onDrawableChangeRef = useRef(onDrawableChange);
  onDrawableChangeRef.current = onDrawableChange;

  useEffect(() => {
    let disposed = false;
    let acquired: GPUDevice | null = null;

    requestGpuDevice().then(resolved => {
      /* Destroyed rather than dropped. A device holds driver resources and nothing else has a
         reference to this one, so an unmount landing before the adapter answers leaks it outright
         - and this view unmounts on every view switch, and again whenever the host's empty state
         flips. `surfaceChartController` releases its own for the same reason. */
      if (disposed) {
        resolved?.destroy();
        return;
      }

      acquired = resolved;
      setDevice(resolved);
      setDeviceState(resolved ? 'ready' : 'absent');
      onDrawableChangeRef.current?.(resolved !== null);

      /**
       * A device can go at any point after it is acquired - a driver reset, a GPU-process crash,
       * one reclaimed from a backgrounded tab - and it goes *silently*: work submitted to a lost
       * device is dropped without throwing. Unobserved, the canvas simply held its last frame
       * while orbit, hover and layout changes did nothing, with no message and no error.
       *
       * Worse here than on the surface chart, which has a software path to fall back to. This has
       * none, so the honest answer is to say so and to stop claiming there is anything to export.
       *
       * `disposed` covers this view's own `destroy()`, which resolves `lost` too: both calls are
       * made after it is set, so a teardown never reports itself as a failure.
       */
      resolved?.lost.then(() => {
        if (disposed) return;
        setDevice(null);
        setDeviceState('lost');
        onDrawableChangeRef.current?.(false);
      });
    });

    return () => {
      disposed = true;
      acquired?.destroy();
    };
  }, []);

  if (deviceState === 'absent' || deviceState === 'lost') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: 24,
          background: theme.css.surface,
          color: theme.css.mutedText,
          font: '13px system-ui, sans-serif',
          textAlign: 'center',
        }}
      >
        {deviceState === 'lost' ? labels.deviceLost : labels.noAdapter}
      </div>
    );
  }

  /* Mounted while the device is still pending, so the first solve overlaps the adapter request
     rather than waiting on it. That solve is the one cost this split cannot remove; every later
     one it removes entirely. */
  return (
    <NetworkScene {...scene} theme={theme} labels={labels} device={device} />
  );
};

type NetworkSceneProps = Omit<Network3DViewProps, 'onDrawableChange'> & {
  /** Null until the adapter answers. The renderer waits; everything else runs. */
  device: GPUDevice | null;
};

/** The canvas and the whole pipeline behind it, which exist only where a frame can be presented. */
const NetworkScene = ({
  graph,
  settings,
  theme,
  camera,
  labels,
  ariaLabel,
  seed,
  onExportCanvasAvailable,
  onCameraCommandsAvailable,
  onMarkLocatorAvailable,
  exportCaptions,
  onNodeActivate,
  foldedBehind,
  query,
  onMatchCountChange,
  onContextMenu,
  device,
}: NetworkSceneProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { containerRef, viewport, deviceViewport, pixelRatio } =
    useGpuCanvas(canvasRef);

  const [hover, setHover] = useState<Hover | null>(null);

  /**
   * Dropped when the graph is replaced, because a hover is an *index* into it.
   *
   * `hoveredNode`, the degree count and the focus set all re-resolve `hover.index` against
   * whatever array arrived, so a rebuild under a stationary pointer leaves the tooltip naming a
   * different component and lights that component's neighbourhood instead. Grouped it is worse
   * than a shift by one: a rule change rewrites the index space wholesale.
   *
   * `onPointerLeave` covers the reader who changed a setting themselves, since they moved the
   * pointer off the canvas to do it. It does not cover the rebuilds nobody in this pane asked
   * for - a formatting rule arriving from another pane, `loadedGraph$` re-emitting, a layout
   * switched by keyboard - and those are the ones that leave a mark reporting as something it is
   * not. Re-established by the next pointer move, which costs nothing; a stale highlight is
   * strictly worse than none.
   *
   * Adjusted during render rather than in an effect, which is React's own guidance for state
   * derived from a prop: an effect would let one frame paint the wrong name first.
   */
  const [hoveredGraph, setHoveredGraph] = useState(graph);
  if (hoveredGraph !== graph) {
    setHoveredGraph(graph);
    setHover(null);
  }

  /* One array, read by the layout and by the packer. The grouped layout reserves a mark's space
     from these, so a second opinion about how large a mark is would be a second opinion about
     whether its group overlaps the one beside it. */
  const nodeSizes = useMemo(
    () => toRelativeNodeSizes(graph.nodes),
    [graph.nodes]
  );

  /* Driven a frame at a time rather than solved in one call - see `useSettlingLayout`. The three
     closed-form arrangements arrive settled and pay nothing for it. */
  const layout = useSettlingLayout({
    graph,
    kind: settings.layoutKind,
    seed,
    iterations: settings.iterations,
    bundleStrength: settings.bundleStrength,
    zStretch: settings.zStretch,
    nodeSizes,
  });

  /**
   * Whether the camera is the reader's now, or still the fit's.
   *
   * `surfaceChartController` carries the same latch for the same reason. The fit is recomputed
   * from the viewport and the layout and handed to `useOrbitInteraction` as its live camera, and
   * that hook applies a changed one through `setCamera`, which *replaces* the camera rather than
   * merging the zoom - so every refit took the reader's yaw, pitch and wheel zoom with it.
   *
   * Which happened on ordinary use, not at some edge: dragging the view-pane divider or resizing
   * the window is a new viewport, and any upstream rebuild - a conditional-formatting change, a
   * traversal edit - is a new `layout` object. The graph snapped back to its opening angle
   * mid-exploration, and on a layout switch it did so underneath the very transition that exists
   * to keep the reader's place.
   *
   * Latched on the reader turning or zooming rather than on the fit having run once, for the
   * reason the surface documents: a first observation at a transient size would otherwise lock a
   * zoom fitted to it, and a pane that starts short and grows would draw the graph in a corner of
   * itself. So the fit follows the pane until someone else has a claim on the camera.
   *
   * The signal comes from `useOrbitInteraction` rather than being reconstructed here. Watching
   * from outside misses the keys: `+`, `=` and `-` dispatch a wheel action straight from
   * `onKeyDown`, producing no React wheel event and touching neither the drag nor the held-key
   * set - so a keyboard-only reader's zoom was the one gesture this could not see, and the next
   * resize took it away. The hook sees every path because it owns every path.
   *
   * Mirrored into a local ref rather than read from the hook's directly, because the hook is
   * constructed *below* the fit it consumes: reading its flag inside this memo is a use before
   * initialisation, which throws on the first render rather than failing subtly. Mirroring is
   * sound because every gesture dispatches, so the render that follows one copies the latch
   * across before any later render can recompute the fit.
   */
  const isCameraOwnedByReader = useRef(false);
  const fittedCameraRef = useRef<ViewCamera | null>(null);

  /**
   * Which group the camera is framing, or `null` for the whole layout.
   *
   * Separate from `fittedCameraRef` because the two answer different questions: that one is *what*
   * camera is held, this one is *what it is held to*. A flight writes a camera through
   * `applyCamera`, which clears the reader-ownership latch by design - so without this the next
   * resize would find no claim on the camera and fit the whole estate again.
   */
  const framedGroupRef = useRef<NetworkNodeId | null>(null);

  /**
   * The camera actually in effect, mirrored for the reason the ownership latch is: the hook that
   * owns it is constructed below the fit that has to read it. `null` until the first render has
   * been through that hook.
   */
  const liveCameraRef = useRef<ViewCamera | null>(null);

  /**
   * Opened at the zoom that frames the whole layout.
   *
   * Multiplied onto the caller's zoom rather than replacing it, so `camera.zoom` stays a relative
   * dial. `fitNetworkZoom` measures at zoom 1 itself, so the two do not compound.
   */
  const fittedCamera = useMemo(() => {
    /* Held at its last value rather than recomputed, so the hook sees an unchanged camera and
       leaves the reader's alone. Returning a fresh-but-equal object would be enough for the
       reducer, which compares by value - this also keeps the memo from churning. */
    const held = fittedCameraRef.current;
    if (held && isCameraOwnedByReader.current) return held;

    const base = toReaderOriented(camera, liveCameraRef.current);

    /* Re-framed for the pane it is now, rather than held at the camera that framed it in the pane
       it was. Holding it would keep the group centred and overflow it the moment the pane got
       smaller, which is the crop a fit held across a resize already had once. The reader asked to
       look at this group, and that outlives the viewport they asked in. */
    const framedGroup = framedGroupRef.current;
    if (framedGroup !== null) {
      const volume = toGroupVolume(layout, graph, framedGroup);
      if (volume) {
        const framed = toFramedGroupCamera(base, viewport, volume);
        fittedCameraRef.current = framed;
        return framed;
      }
      /* The region has gone - collapsed, or the grouping changed - so there is nothing left to
         hold the camera to and the whole layout is the honest answer. */
      framedGroupRef.current = null;
    }

    const fitted = toFittedCamera(base, viewport, layout);
    fittedCameraRef.current = fitted;
    return fitted;
  }, [camera, viewport, layout, graph]);

  /* What a fit would be computed from, read at the moment one is asked for rather than closed over
     - the control is published once and pressed much later. */
  const fitInputs = useRef({ camera, viewport, layout, graph });
  fitInputs.current = { camera, viewport, layout, graph };

  /* Declared after the fit, which it consumes, and before everything that reads the live camera.
     The hook treats its argument as a live input rather than a seed, so a refit - a resize, or a
     switch of layout - is applied rather than ignored. */
  const {
    orbit,
    isTurning,
    hasReaderMovedCamera,
    hasReaderTakenCamera,
    bindCanvas,
    applyCamera,
    canvasProps,
    toLocalPoint,
  } = useOrbitInteraction(canvasRef, fittedCamera);

  /**
   * Records what a flight puts in effect, as it puts it there.
   *
   * The held camera has to be one the view actually reached, not one it set out for. A flight can
   * be given way to halfway, and holding the goal would mean the next resize handed the reader a
   * framing they had scrolled out of - jerking them into the group they had just left.
   */
  const applyFlightCamera = useCallback(
    (next: ViewCamera) => {
      fittedCameraRef.current = next;
      applyCamera(next);
    },
    [applyCamera]
  );

  const { flyTo } = useCameraFlight({
    camera: orbit.camera,
    applyCamera: applyFlightCamera,
    hasReaderTakenCamera,
  });

  /**
   * The same lookup the fit memo uses, against what is on screen at the moment a host asks.
   *
   * Read through `fitInputs` rather than closed over, because the commands below are published
   * once and called much later. Shared with `canFrameGroup` so that what a host is told it can do
   * and what doing it finds cannot disagree.
   */
  const toFrameableVolume = useCallback(
    (nodeId: NetworkNodeId): NetworkVolume | undefined => {
      const { layout, graph } = fitInputs.current;
      return toGroupVolume(layout, graph, nodeId);
    },
    []
  );

  /**
   * Published the moment there is a view, rather than once something has been drawn.
   *
   * The export hook waits for a canvas because it copies pixels. This moves a camera, which is
   * this component's own state and exists from the first render - and a host that registers its
   * header controls on mount would otherwise register a dead one.
   *
   * Commanded rather than supplied. `fittedCamera` is a value the hook deduplicates against the
   * last one it was given, which is what lets a re-render hand in a recomputed fit without
   * stamping on the reader's rotation - and what would make a second press, which computes the
   * same value as the first, do nothing at all.
   */
  useEffect(() => {
    const publish = onCameraCommandsAvailable;
    publish?.({
      fit: () => {
        const { camera, viewport, layout } = fitInputs.current;
        const fitted = toFittedCamera(
          toReaderOriented(camera, liveCameraRef.current),
          viewport,
          layout
        );
        /* Recorded as the held fit too, so the memo hands back what is actually on screen rather
           than the framing from whenever the reader first took the camera. */
        fittedCameraRef.current = fitted;
        framedGroupRef.current = null;
        applyCamera(fitted);
      },
      /**
       * Frames one group, by the id of the node its region stands for.
       *
       * By id rather than by index, because an index belongs to one render and the host is
       * answering a menu the reader opened some renders ago. A group whose region is no longer
       * drawn - collapsed, or the grouping changed under the open menu - is simply not flown to,
       * which is the honest answer to "frame something that is not there".
       */
      frameGroup: nodeId => {
        const { camera, viewport } = fitInputs.current;
        const volume = toFrameableVolume(nodeId);
        if (!volume) return;

        const framed = toFramedGroupCamera(
          toReaderOriented(camera, liveCameraRef.current),
          viewport,
          volume
        );
        /* Recorded as what the camera is held to, so a resize afterwards re-frames this group
           rather than snapping back to the whole estate. The held *camera* is not set here: the
           flight writes it as it travels, because what is held has to be somewhere the view got
           to. */
        framedGroupRef.current = nodeId;
        flyTo(framed);
      },
      canFrameGroup: nodeId => toFrameableVolume(nodeId) !== undefined,
    });
    return () => publish?.(null);
  }, [onCameraCommandsAvailable, applyCamera, flyTo, toFrameableVolume]);

  /* Mirrored rather than latched, which is what lets the fit control hand the camera back. The
     hook clears its own flag when a host sets a camera, so a one-way copy would leave this true
     for good and the view would go on framing to whatever viewport it had when the reader first
     orbited - cropping the graph on the next resize. Writing a pure function of this render's
     value is still idempotent under a double-invoked render, which is why it is written here
     rather than in an effect. */
  isCameraOwnedByReader.current = hasReaderMovedCamera;
  liveCameraRef.current = orbit.camera;

  /**
   * The arrangement on screen, which during a re-arrangement is between two layouts.
   *
   * Only for the layouts a movement can mean something in. The two force solves are seeded and
   * iterative, so the same graph with one component added settles somewhere unrelated - and
   * interpolating between two unrelated arrangements sends every mark a long way to say nothing.
   * Turning it on for those needs the solve warm-started from the previous positions first, which
   * is a change to `runForceLayout` rather than to this.
   */
  const isTransitionable =
    settings.layoutKind === 'grouped' ||
    settings.layoutKind === 'bundled-sphere' ||
    settings.layoutKind === 'cone-tree';

  /* Match keys rather than node ids: a grouping renames every mark, and the movement has to
     recognise the component behind the mark to move it rather than replace it. */
  const matchKeys = useMemo(
    () => graph.nodes.map(({ matchKey }) => matchKey),
    [graph.nodes]
  );

  const { positions: transitionedPositions, isMoving } = useLayoutTransition({
    ids: matchKeys,
    positions: layout.positions,
    links: layout.links,
    isEnabled: isTransitionable,
  });

  /**
   * The layout as drawn, which is the target arrangement except mid-transition.
   *
   * References become straight segments between the travelling marks while the graph moves, and
   * the bundled routes come back when it settles. Two reasons, and the cheap one is not the main
   * one: re-splining twenty sampled points per reference along shared paths every frame is the
   * expensive half of the frame, but leaving the routes at their target is worse than that - the
   * edges detach from the marks they join and the picture comes apart for half a second. Nobody
   * reads bundle structure while it is moving; everybody notices an edge that ends in mid-air.
   *
   * A region follows the marks inside it, which is the same principle one step up: a group is
   * where its members are, so its cloud is centred on them at every frame rather than waiting at
   * the destination for them to arrive. Its radius is not interpolated, so a region that is
   * gaining or losing members is briefly the size it is about to be - visible only on a regroup
   * that changes a group's contents wholesale, and a good deal less visible than a cloud sitting
   * somewhere none of its members are yet.
   *
   * Guides move where they are a function of the positions, which `guideLinks` is how a layout
   * says. The radial tree's skeleton is exactly `[positions[parent], positions[child]]`, and it is
   * the one layout whose guides *are* the picture - left at its target it drew the structure of an
   * arrangement not yet on screen, every segment ending in empty space, which is the same failure
   * the routes are re-derived to avoid. The bundled sphere's cage is a constant and the layer grids
   * are planes rather than pairs; neither has anything to move.
   */
  const drawnLayout = useMemo(() => {
    if (!isMoving) return layout;

    const positions = [...transitionedPositions];
    return {
      ...layout,
      positions,
      routes: layout.links.map(({ source, target }) => [
        positions[source],
        positions[target],
      ]),
      guides: layout.guideLinks
        ? layout.guideLinks.map(({ source, target }) => [
            positions[source],
            positions[target],
          ])
        : layout.guides,
      volumes: layout.volumes.map(volume => ({
        ...volume,
        center: toCentroid(volume.markIndices, positions) ?? volume.center,
      })),
    };
  }, [layout, transitionedPositions, isMoving]);

  const [treeCollapsedIds, setTreeCollapsedIds] = useState<
    ReadonlySet<NetworkNodeId>
  >(() => new Set());
  const canActivate = onNodeActivate !== undefined;
  const isSearching = (query ?? '').trim() !== '';
  /**
   * Everything, while a search is running.
   *
   * A row folded in the list is a disclosure, not a deletion: the marks under it are still drawn.
   * Filtered against a tree built with `treeCollapsedIds`, a search would answer "no matches"
   * about something in plain view - and folding a row mid-search would drop results that had
   * already been found, which `toRenumbered` then makes unreachable by rewriting that row to a
   * leaf.
   */
  const treeItems = useMemo(
    () =>
      toNetworkTreeItems({
        graph,
        foldedBehind,
        members: labels.members,
        collapsedIds: isSearching ? NO_COLLAPSED_IDS : treeCollapsedIds,
        canActivate,
      }),
    [
      graph,
      foldedBehind,
      labels.members,
      isSearching,
      treeCollapsedIds,
      canActivate,
    ]
  );
  /**
   * The tree the query leaves, and the marks it answers with.
   *
   * One query drives both, so the list a keyboard reader tabs into cannot disagree with what is
   * lit on the canvas beside it.
   */
  /* The fold state is handed to the filter rather than to the tree builder, so closing a row while
     a search is running narrows the list without taking anything out of the results or off the
     canvas - and the key that closed it still does something visible. */
  const found = useMemo(
    () => toMarkFilter(treeItems, query ?? '', treeCollapsedIds),
    [treeItems, query, treeCollapsedIds]
  );

  const matchCountRef = useRef(onMatchCountChange);
  matchCountRef.current = onMatchCountChange;
  useEffect(() => {
    matchCountRef.current?.(found.matchedIds.length);
  }, [found.matchedIds]);

  const searchFocus = useMemo((): NetworkFocus | null => {
    if (!isSearching) return null;
    return {
      kind: 'marks',
      indices: toNetworkMarkIndices({
        nodes: graph.nodes,
        volumes: layout.volumes,
        ids: found.matchedIds,
      }),
    };
  }, [isSearching, graph.nodes, layout.volumes, found.matchedIds]);

  const treeIds = useMemo(() => found.items.map(({ id }) => id), [found.items]);
  const { activeId, setActiveId, highlightedId, containerFocusProps } =
    useMarkFocus(treeIds);

  const treeHandlers = useRef({ onNodeActivate, onContextMenu, treeItems });
  treeHandlers.current = { onNodeActivate, onContextMenu, treeItems };

  const activateTreeItem = useCallback(
    (nodeId: NetworkNodeId) => treeHandlers.current.onNodeActivate?.(nodeId),
    []
  );
  const toggleTreeItem = useCallback((nodeId: NetworkNodeId) => {
    const handlers = treeHandlers.current;
    const toggle = toTreeToggle({
      item: handlers.treeItems.find(({ id }) => id === nodeId),
      canActivate: handlers.onNodeActivate !== undefined,
    });
    if (toggle === 'activate') handlers.onNodeActivate?.(nodeId);
    if (toggle === 'tree') setTreeCollapsedIds(ids => toggleId(ids, nodeId));
  }, []);
  const openTreeItemMenu = useCallback(
    (nodeId: NetworkNodeId, at: { clientX: number; clientY: number }) =>
      treeHandlers.current.onContextMenu?.({ kind: 'node', nodeId }, at),
    []
  );

  const treeColors = useMemo<MarkListColors>(
    () => ({
      surface: theme.css.surface,
      ink: theme.css.text,
      mutedInk: theme.css.mutedText,
      border: theme.css.border,
    }),
    [theme.css.surface, theme.css.text, theme.css.mutedText, theme.css.border]
  );

  const treeFocus = useMemo(
    () =>
      toNetworkTreeFocus({
        nodes: graph.nodes,
        volumes: layout.volumes,
        nodeId: highlightedId,
      }),
    [graph.nodes, layout.volumes, highlightedId]
  );
  /**
   * The hover, once the pointer has stopped moving over it.
   *
   * Everything a hover changes on screen hangs off this rather than off the raw pointer: the
   * dimming, the tooltip, the drawn-up references. Crossing a cluster at speed used to repack the
   * whole estate once per mark passed over, which reads as the picture flickering rather than as
   * the view answering - and the answer was never for any of those marks anyway.
   *
   * The cursor is the exception, and stays immediate. It is how the view says "there is something
   * here", and that is worth saying the moment it is true.
   */
  const settled = useSettledHover({
    hovered: hover,
    /* The target rather than the whole hover: `onPointerMove` keeps this object while the pointer
       stays on the same thing, so the wait counts from arriving rather than from stopping. */
    identity: hover?.target,
    subject: graph,
  });
  const resting = settled.target;

  /**
   * What recedes and what does not, in the order a reader's attention goes.
   *
   * A hover is momentary and wins outright. A row the keyboard is on wins over a search because
   * stepping through the results is how a reader reads them - and stepping is only possible while
   * focus is in the chart, which is exactly when `treeFocus` is set. Otherwise the search stands,
   * so a reader who typed into the field and let go of it is left looking at every answer at once.
   */
  const focus = resting?.target ?? treeFocus ?? searchFocus;

  /**
   * Repacked when the focus changes, which rewrites every instance.
   *
   * Cheaper than it sounds and simpler than the alternative: emphasis lives on the instance, so a
   * partial update would have to know which slots moved. At a few thousand instances the whole
   * repack is well under a frame, and it only runs when the hovered node actually changes rather
   * than on every pointer move.
   */
  const instances = useMemo(
    () =>
      packNetworkInstances({
        graph,
        layout: drawnLayout,
        theme,
        focus,
        nodeSizes,
        nodeScale: settings.nodeScale,
        edgeWidth: settings.edgeWidth,
        guideWidth: settings.guideWidth,
        edgeOpacity: settings.edgeOpacity,
        groupCloudDensity: settings.groupCloudDensity,
      }),
    [
      graph,
      drawnLayout,
      theme,
      focus,
      nodeSizes,
      settings.nodeScale,
      settings.edgeWidth,
      settings.guideWidth,
      settings.edgeOpacity,
      settings.groupCloudDensity,
    ]
  );

  /* The renderer's arithmetic is linear and the theme is authored encoded, so the rig and the
     surface convert once per theme rather than once per frame. */
  const linearLighting = useMemo(
    () => toLinearLighting(theme.lighting),
    [theme.lighting]
  );
  const linearSurface = useMemo(
    (): readonly [number, number, number] => [
      srgbToLinear(theme.surface[0]),
      srgbToLinear(theme.surface[1]),
      srgbToLinear(theme.surface[2]),
    ],
    [theme.surface]
  );

  const [renderer, setRenderer] = useState<NetworkGpuRenderer | null>(null);

  /**
   * Whether a frame has reached the canvas.
   *
   * Not answerable by measuring: a canvas that has never been drawn to carries the HTML default
   * 300x150 backing store, which is indistinguishable from a small view - so exporting one would
   * save a blank PNG of that size rather than produce the missing-canvas error the exporter
   * reports. Same reason `useChartCanvas` exposes `hasPainted`.
   */
  const hasDrawn = useRef(false);
  /** One origin for the view's whole life, so the pulses never restart. */
  const flowOrigin = useRef(performance.now());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!device || !canvas) return;

    const created = createNetworkGpuRenderer({ device, canvas });
    setRenderer(created);

    return () => {
      setRenderer(null);
      created.destroy();
    };
    /* Keyed on the device alone. The canvas element is stable for the component's life once the
       device has resolved, and a WebGPU context cannot be reconfigured onto a different canvas
       anyway - so a renderer outlives everything except the device it was made from. */
  }, [device]);

  useEffect(() => {
    renderer?.setInstances(instances);
  }, [renderer, instances]);

  /**
   * Zero while nothing is focused, which is what keeps the view still.
   *
   * Read once rather than subscribed to. A reader who changes the system setting mid-session gets
   * the new answer on the next rebuild of the instances, which is the same bargain
   * `useSettlingLayout` makes, and the alternative is a media-query listener on every view for a
   * setting that changes about never.
   */
  const pulseStrength =
    instances.pulseCount > 0 && !prefersReducedMotion() ? 1 : 0;

  /**
   * One frame per animation frame, at most - and only repeatedly while something is pulsing.
   *
   * Every pointer move during an orbit re-renders, which would otherwise submit a command buffer
   * per move - three in a frame the display can only show one of. Cancelling any frame still
   * pending collapses the burst into the one draw that is visible.
   *
   * The loop reschedules itself only when there is motion to carry, so an unfocused graph costs
   * exactly what it did before: one frame per change. `requestAnimationFrame` stops being served
   * on a hidden tab, so a view left focused in a background tab stops too.
   */
  useEffect(() => {
    if (!renderer) return;
    /**
     * On `viewport`, not on `deviceViewport`.
     *
     * `useGpuCanvas` floors the device size at one pixel so a render attachment is never allocated
     * at zero, which is invalid - so a test against `deviceViewport` can never be true and this
     * guard did nothing. Meanwhile the sizing layout effect *does* bail at a zero CSS size, leaving
     * the canvas at the HTML default 300x150 while this drew at 1x1: a resolve-target size
     * mismatch, which WebGPU rejects outright rather than downgrading, so the frame was dropped and
     * an uncaptured error reached the console. `hasDrawn` was set anyway, so the export hook then
     * handed back a blank 300x150 canvas instead of the missing-canvas error it documents.
     *
     * Reachable on an ordinary mount: a pane that starts collapsed, a `display: none` ancestor, or
     * a device that resolves before the first `ResizeObserver` callback.
     */
    if (viewport.width === 0 || viewport.height === 0) return;

    let pendingFrame: number | undefined;
    const render = (now: number) => {
      pendingFrame = undefined;
      renderer.draw({
        camera: orbit.camera,
        viewport: deviceViewport,
        pixelRatio,
        lighting: linearLighting,
        surface: linearSurface,
        ambientOcclusion: theme.ambientOcclusion,
        edgeBlend: theme.edgeBlend,
        flow: {
          /* Measured from one origin for the life of the view rather than from the start of this
             effect, which runs again on every pointer move. Restarted per effect, the pulses
             would jump back to the source each time the reader moved the mouse. */
          phase: ((now - flowOrigin.current) / 1000) * FLOW_PIXELS_PER_SECOND,
          pulseStrength,
          leadStrength: EDGE_LEAD_STRENGTH,
        },
      });
      hasDrawn.current = true;
      if (pulseStrength > 0) pendingFrame = requestAnimationFrame(render);
    };
    pendingFrame = requestAnimationFrame(render);

    return () => {
      if (pendingFrame !== undefined) cancelAnimationFrame(pendingFrame);
    };
  }, [
    pulseStrength,
    renderer,
    instances,
    orbit.camera,
    viewport,
    deviceViewport,
    pixelRatio,
    linearLighting,
    linearSurface,
    theme.edgeBlend,
    theme.ambientOcclusion,
  ]);

  /* Read through a ref so the captions changing does not re-publish the hook, which would have
     the host re-register its whole view pane. */
  const exportCaptionsRef = useRef(exportCaptions);
  exportCaptionsRef.current = exportCaptions;

  /**
   * Publishes a hook that copies the last presented frame.
   *
   * `drawImage` from a WebGPU canvas reads whatever was last presented, which is exactly what is
   * on screen - there is no separate readback and no second render. It does mean the copy is of
   * the current camera rather than of a canonical angle, which for an orbited 3D view is the
   * honest thing to export: the reader exports the view they arranged.
   */
  useEffect(() => {
    const publish = onExportCanvasAvailable;
    publish?.(() =>
      hasDrawn.current
        ? toExportCanvas({
            canvas: canvasRef.current,
            backgroundColor: theme.css.surface,
            inkColor: theme.css.text,
            captions: exportCaptionsRef.current ?? [],
          })
        : null
    );
    return () => publish?.(null);
  }, [onExportCanvasAvailable, theme.css.surface, theme.css.text]);

  /**
   * Published once, reading the current frame through refs.
   *
   * The locator has to answer about the camera and the arrangement as they are when it is asked -
   * both move, through an orbit and through a settling solve - so publishing a new pair of
   * functions per frame would mean re-registering per frame. The functions are stable and the
   * state they read is not.
   *
   * `orbit.camera` and not the `camera` prop, which is the host's seed: it carries neither the fit
   * zoom nor any of the reader's own turning. Every other projection in this scene reads the orbit
   * - and `hitTest` picks the component a dropped reference is made against, so the seed camera
   * resolved a release over a visible mark to a different component, or to none at all.
   */
  const locatorState = useRef({
    instances,
    camera: orbit.camera,
    viewport,
    graph,
  });
  locatorState.current = { instances, camera: orbit.camera, viewport, graph };

  useEffect(() => {
    const publish = onMarkLocatorAvailable;
    const toClientOffset = () => canvasRef.current?.getBoundingClientRect();

    publish?.({
      at: ({ clientX, clientY }) => {
        const box = toClientOffset();
        if (!box) return null;
        const {
          instances: packed,
          camera: eye,
          viewport: size,
          graph: drawn,
        } = locatorState.current;
        const index = findNetworkNodeAt({
          instances: packed,
          camera: eye,
          viewport: size,
          point: { x: clientX - box.left, y: clientY - box.top },
          tolerance: HIT_TOLERANCE,
        });
        return index === null ? null : (drawn.nodes[index]?.id ?? null);
      },
      locate: nodeId => {
        const box = toClientOffset();
        if (!box) return null;
        const {
          instances: packed,
          camera: eye,
          viewport: size,
          graph: drawn,
        } = locatorState.current;
        const index = drawn.nodes.findIndex(({ id }) => id === nodeId);
        const point = locateNetworkMark({
          instances: packed,
          camera: eye,
          viewport: size,
          index,
        });
        return point && { x: point.x + box.left, y: point.y + box.top };
      },
    });

    return () => publish?.(null);
  }, [onMarkLocatorAvailable]);

  /**
   * The labels on screen now, recomputed as the camera moves.
   *
   * Every frame of an orbit, which is the cost of placing text against a projection - a label
   * belongs to a point in the scene, and the scene turns. Bounded instead: the declutter caps
   * what it returns, so the list it reconciles is tens of elements rather than one per mark, and
   * it is skipped outright while the setting is off.
   */
  const labels3d = useMemo(
    () =>
      settings.showLabels
        ? placeNetworkLabels({
            graph,
            layout: drawnLayout,
            instances,
            camera: orbit.camera,
            viewport,
            /* `focus`, not the pointer alone: the renderer dims and lights off the same value, so
               a keyboard reader moving through the mark list gets the mark lit and would have
               watched its label lose the declutter to a larger neighbour. */
            focus,
            maximum: MAXIMUM_LABELS,
            fontSize: LABEL_FONT_SIZE,
            maximumWidth: LABEL_MAX_WIDTH,
          })
        : [],
    [
      settings.showLabels,
      graph,
      drawnLayout,
      instances,
      orbit.camera,
      viewport,
      focus,
    ]
  );

  /**
   * The node a target is about, and the marks it stands for.
   *
   * One resolution for both kinds, because everything the tooltip says is a function of these
   * two: a mark is its own node and its own single mark, and a region is the node it stands for
   * over everything inside it. Resolved against the live `graph` rather than captured with the
   * hover, for the reason the `hoveredGraph` reset gives - a stale index names the wrong thing.
   */
  const hovered = useMemo((): {
    node: NetworkNode;
    markIndices: readonly number[];
  } | null => {
    const target = resting?.target;
    if (target === undefined) return null;

    if (target.kind === 'node') {
      const node = graph.nodes[target.index];
      return node ? { node, markIndices: [target.index] } : null;
    }
    /* A reference has no single node to be about - see `hoveredReference`, which answers for it
       instead. Returned as nothing rather than fallen through, because `target.index` means a
       route there and would index the volumes. */
    if (target.kind === 'edge') return null;
    /* Nor is a set of search results, which the tree beside the canvas already lists. */
    if (target.kind === 'marks') return null;

    const volume = layout.volumes[target.index];
    const node = volume ? graph.nodes[volume.nodeIndex] : undefined;
    return volume && node ? { node, markIndices: volume.markIndices } : null;
  }, [resting?.target, graph.nodes, layout.volumes]);

  /**
   * The reference under the pointer, as the pair it joins.
   *
   * Its own derivation rather than a third shape on `hovered`, because what a reader wants from a
   * reference is not what `hovered` carries. A mark's tooltip answers "what is this and how
   * connected is it"; a reference's answers "what does this join", which is the question that is
   * impossible to read off a hairball by eye - and the two ends are the answer, not the type.
   *
   * Through `edgeIndices` rather than by indexing `graph.edges` with the route's own index: the
   * two are not parallel, so a dropped self-loop earlier in the list would shift every reference
   * after it onto the wrong one.
   */
  const hoveredReference = useMemo(() => {
    const target = resting?.target;
    if (target?.kind !== 'edge') return null;

    const link = drawnLayout.links[target.index];
    const edgeIndex = drawnLayout.edgeIndices[target.index];
    if (link === undefined || edgeIndex === undefined) return null;

    const source = graph.nodes[link.source];
    const sink = graph.nodes[link.target];
    return source && sink
      ? { source, sink, typeName: graph.edges[edgeIndex]?.typeName ?? '' }
      : null;
  }, [resting?.target, graph.nodes, graph.edges, drawnLayout]);

  /**
   * References with exactly one end in the hovered set.
   *
   * Exactly one rather than at least one, which for a mark is the same number and for a region is
   * the difference between "how this group connects to the estate" and "how this group connects
   * to the estate plus how it is wired internally". The first is the reading a reader wants from
   * a group; the second buries it under whatever the group happens to contain.
   */
  const degree = useMemo(() => {
    if (hovered === null) return 0;
    const marks = new Set(hovered.markIndices);
    return layout.links.reduce(
      (total, { source, target }) =>
        total + (marks.has(source) !== marks.has(target) ? 1 : 0),
      0
    );
  }, [hovered, layout.links]);

  /* Only for a mark. A region is an *open* group - everything in it is already on screen - so a
     thumbnail of it would be a smaller copy of what the reader is looking at. */
  const folded =
    resting?.target.kind === 'node' && hovered
      ? (foldedBehind?.(hovered.node.id) ?? null)
      : null;

  /**
   * What a region contains, counted through the marks rather than by counting them.
   *
   * `markIndices.length` is how many marks are drawn inside it, which is not how many components
   * are in the group once anything inside is closed: since a closed group is drawn as a mark, a
   * subgroup of ten counted as one, and collapsing a nested group changed the number its parent
   * reported.
   */
  const regionCount = useMemo(() => {
    if (resting?.target.kind !== 'region' || hovered === null) return 0;
    return toAccountedMarkCount(hovered.markIndices, graph.nodes, foldedBehind);
    /* `foldedBehind` is a fresh closure per render of the host, and depending on it would
       recompute this on every pointer move. What it answers is a function of the graph. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resting?.target.kind, hovered, graph.nodes]);

  /**
   * The line under the name, assembled from whatever the target has to say.
   *
   * Parts rather than a branch per case, because the cases overlap: a grouping rule can group
   * *by* a component, so a closed group can have a type name and a member count and a reference
   * count all at once, and a branch that picked one of them threw away two.
   */
  const isRegion = resting?.target.kind === 'region';
  const memberCount = isRegion ? regionCount : (folded?.foldedCount ?? null);
  const summary = [
    hovered?.node.typeName,
    memberCount === null ? '' : labels.members(memberCount),
    labels.degree(degree),
  ]
    .filter(part => part !== undefined && part !== '')
    .join(' · ');

  const resolveTarget = (point: {
    x: number;
    y: number;
  }): NetworkFocus | null => {
    const index = findNetworkNodeAt({
      instances,
      camera: orbit.camera,
      viewport,
      point,
      tolerance: HIT_TOLERANCE,
    });
    if (index !== null) return { kind: 'node', index };

    /* Marks, then references, then regions - the order `resolveContextTarget` uses, and for the
       same reason: a region is almost always also around the other two, and a reference passes
       through the marks it joins. */
    const routeIndex = findNetworkRouteAt({
      layout: drawnLayout,
      camera: orbit.camera,
      viewport,
      point,
      tolerance: ROUTE_HIT_TOLERANCE,
    });
    if (routeIndex !== null) return { kind: 'edge', index: routeIndex };

    const volumeIndex = findNetworkVolumeAt({
      instances,
      camera: orbit.camera,
      viewport,
      point,
    });
    return volumeIndex === null ? null : { kind: 'region', index: volumeIndex };
  };

  /**
   * What a secondary click landed on, resolved marks first, then references, then regions.
   *
   * Narrowest target wins, which is the only thing that makes the small ones reachable: a region
   * is almost always also *around* the other two, and a reference passes through the marks it
   * joins. Same order the hover resolves in, with references inserted.
   *
   * Against `drawnLayout` and not `layout`, and that is load-bearing. `instances` are packed from
   * the drawn one, so the marks and the regions already answer for what is on screen - and while
   * a re-arrangement is running the drawn routes are straight segments between the travelling
   * marks while `layout.routes` are the destination splines, which are not on screen yet. Read
   * off the target, a right-click on empty canvas could land on an invisible route and open
   * Delete reference for a reference the reader cannot see.
   */
  const resolveContextTarget = (point: {
    x: number;
    y: number;
  }): NetworkContextTarget | null => {
    const nodeIndex = findNetworkNodeAt({
      instances,
      camera: orbit.camera,
      viewport,
      point,
      tolerance: HIT_TOLERANCE,
    });
    const node = nodeIndex === null ? undefined : graph.nodes[nodeIndex];
    if (node) return { kind: 'node', nodeId: node.id };

    const routeIndex = findNetworkRouteAt({
      layout: drawnLayout,
      camera: orbit.camera,
      viewport,
      point,
      tolerance: ROUTE_HIT_TOLERANCE,
    });
    if (routeIndex !== null) {
      /* Through `edgeIndices`, which is what makes this an entry of `graph.edges` rather than of
         the surviving routes - the two are not parallel. Carried unchanged onto `drawnLayout`,
         whose routes are rebuilt from the same `links`. */
      const edgeIndex = drawnLayout.edgeIndices[routeIndex];
      return edgeIndex === undefined ? null : { kind: 'edge', edgeIndex };
    }

    const volumeIndex = findNetworkVolumeAt({
      instances,
      camera: orbit.camera,
      viewport,
      point,
    });
    const group =
      volumeIndex === null
        ? undefined
        : graph.nodes[drawnLayout.volumes[volumeIndex]?.nodeIndex ?? -1];
    return group ? { kind: 'node', nodeId: group.id } : null;
  };

  return (
    <div
      ref={containerRef}
      {...containerFocusProps}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: theme.css.surface,
      }}
    >
      <canvas
        {...canvasProps}
        ref={bindCanvas}
        aria-label={ariaLabel}
        /**
         * A pointer wherever the pointer gets a response, which is any mark or any region.
         *
         * `useOrbitInteraction` sets `grab`/`grabbing` - right for a canvas whose whole surface
         * turns the camera, wrong over a mark, where a hand says "drag me" about the one place
         * dragging is not the point.
         *
         * Hover rather than click, deliberately. A leaf component answers a hover with a tooltip
         * and a lit neighborhood, and a double-click with nothing, since only a group folds.
         * Keyed on activation this would be `onNodeActivate && (isRegion || folded)`, and most
         * marks in most viewpoints would keep the hand.
         *
         * Merged onto the hook's style so `touchAction`, the focus ring and the drag cursor
         * survive, and suppressed while turning for the same reason hit-testing is.
         */
        style={{
          ...canvasProps.style,
          cursor:
            !isTurning && hover !== null ? 'pointer' : canvasProps.style.cursor,
        }}
        onPointerMove={event => {
          canvasProps.onPointerMove(event);

          /* Hit-testing is suspended while the graph is turning. Resolving it against a camera
             that has already moved on jumps the highlight between nodes under a cursor that has
             not moved, and repacking the instances mid-drag stalls the frame it is competing
             with. */
          if (isTurning) return;

          const point = toLocalPoint(event);
          const target = resolveTarget(point);

          /* The anchor follows the cursor even while the same thing stays hovered, so the tooltip
             does not stay where the pointer first entered it. That re-renders on every move over
             a target, and the previous target object is *kept* when it names the same thing, so
             everything memoised on it - the repack above all - runs only when what is hovered
             actually changes rather than on every pixel of movement across one mark. */
          setHover(current =>
            target === null
              ? null
              : {
                  target:
                    current !== null && isSameFocus(current.target, target)
                      ? current.target
                      : target,
                  at: point,
                }
          );
        }}
        onPointerDown={event => {
          canvasProps.onPointerDown(event);
          setActiveId(null);
        }}
        onPointerLeave={() => setHover(null)}
        /**
         * `preventDefault` whatever was hit, including nothing.
         *
         * The browser's own menu over a canvas offers to save or copy an image of the whole pane,
         * which is not the reference the reader right-clicked, and a view that sometimes shows it
         * and sometimes does not is worse than one that never does.
         */
        onContextMenu={event => {
          event.preventDefault();
          if (!onContextMenu) return;

          const target = resolveContextTarget(toLocalPoint(event));
          if (target === null) return;
          onContextMenu(target, {
            clientX: event.clientX,
            clientY: event.clientY,
          });
        }}
        /**
         * Resolved from the event rather than from `hover`, and for a reason the two look
         * interchangeable for: a double-click arrives from a pointing device that never produced
         * a move, which is every touch and every synthetic click. Reading the latched hover there
         * activates whatever was last under the pointer, or nothing at all.
         */
        onDoubleClick={event => {
          if (!onNodeActivate) return;
          const target = resolveTarget(toLocalPoint(event));
          if (target === null) return;

          const nodeIndex = toTargetNodeIndex(target, drawnLayout.volumes);
          const node =
            nodeIndex === undefined ? undefined : graph.nodes[nodeIndex];
          if (node) onNodeActivate(node.id);
        }}
      />

      {treeItems.length > 0 && (
        <MarkList
          kind="tree"
          label={labels.markTree}
          items={found.items}
          activeId={activeId}
          onActiveChange={setActiveId}
          onActivate={activateTreeItem}
          onExpand={toggleTreeItem}
          onCollapse={toggleTreeItem}
          onContextMenu={onContextMenu ? openTreeItemMenu : undefined}
          colors={treeColors}
        />
      )}

      {/* Behind the tooltip in the stack and inert to the pointer: the labels explain the picture,
          and a label that swallowed a click would take the mark underneath it with it. */}
      {labels3d.map(({ key, text, x, y, kind }) => (
        <div
          key={key}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: x,
            top: y,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            maxWidth: LABEL_MAX_WIDTH,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            font: `${kind === 'region' ? 600 : 400} ${LABEL_FONT_SIZE}px system-ui, sans-serif`,
            color: kind === 'region' ? theme.css.text : theme.css.mutedText,
            /* Carries the text over whatever it crosses without a plate behind it, which at this
               density would be a field of boxes. The surface is the theme's, so this darkens on a
               dark background and lightens on a light one. */
            textShadow: `0 0 3px ${theme.css.surface}, 0 0 3px ${theme.css.surface}, 0 0 3px ${theme.css.surface}`,
          }}
        >
          {text}
        </div>
      ))}

      {resting && (hovered || hoveredReference) && (
        /**
         * No live role, matching the other charts' tooltips.
         *
         * It carried `role="status"`, which is `aria-live="polite"` - and its content changes on
         * every new hover, so a pointer sweeping a dense cluster queued one announcement per mark
         * it crossed. Unusable chatter for anyone reading something else, and it was the only
         * live region in the package.
         *
         * Nothing is lost by dropping it, because the announcement never reached the people a
         * live region is for: this is driven by `onPointerMove` alone, and the canvas already
         * carries the estate's totals through its `aria-label`.
         *
         * A keyboard reader reaches one mark or one region through the tree after the canvas.
         */
        <div
          style={{
            position: 'absolute',
            left: resting.at.x + 14,
            top: resting.at.y + 14,
            transform: tooltipFlipTransform({ at: resting.at, viewport }),
            pointerEvents: 'none',
            maxWidth: 'min(320px, 60%)',
            padding: '6px 8px',
            borderRadius: 6,
            border: `1px solid ${theme.css.border}`,
            background: theme.css.surface,
            color: theme.css.text,
            font: '12px system-ui, sans-serif',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.35)',
            opacity: settled.isShown ? 1 : 0,
            transition: `opacity ${HOVER_FADE}ms ease-out`,
          }}
        >
          {hoveredReference ? (
            <>
              <div style={{ fontWeight: 600 }}>
                {hoveredReference.source.label} → {hoveredReference.sink.label}
              </div>
              {hoveredReference.typeName !== '' && (
                <div style={{ color: theme.css.mutedText }}>
                  {hoveredReference.typeName}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontWeight: 600 }}>{hovered?.node.label}</div>
              <div style={{ color: theme.css.mutedText }}>{summary}</div>
              {folded?.graph && device && (
                <div style={{ marginTop: 6 }}>
                  <NetworkMiniature
                    graph={folded.graph}
                    theme={theme}
                    device={device}
                    width={MINIATURE_SIZE.width}
                    height={MINIATURE_SIZE.height}
                    seed={seed}
                  />
                </div>
              )}
              {onNodeActivate && (isRegion || folded) && (
                <div
                  style={{
                    marginTop: 6,
                    color: theme.css.mutedText,
                    fontSize: 11,
                  }}
                >
                  {folded ? labels.expandHint : labels.collapseHint}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
